import { useEffect, useState } from "react";
import { X, Star, Loader2, CloudHail, CloudFog, TriangleAlert, Navigation } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { http, distKm } from "@/lib/api";
import { WEATHER_CODES, riskLevel, hailLevel, fogLevel, roadReasons } from "@/lib/params";
import { REPORT_TYPES } from "@/lib/reportTypes";
import { WeekView } from "@/components/weather/WeekView";
import { useI18n } from "@/lib/i18n";

const Stat = ({ label, value, unit, testid }) => {
  const { t } = useI18n();
  return (
  <div className="rounded-xl border border-white/5 bg-white/[0.03] p-3">
    <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{t(label)}</div>
    <div data-testid={testid} className="mt-1 font-heading text-2xl font-bold text-white">
      {value ?? "—"}<span className="ml-1 text-xs font-normal text-slate-400">{value != null ? t(unit) : ""}</span>
    </div>
  </div>
  );
};

const Badge = ({ icon: Icon, label, color, sub, testid }) => (
  <div data-testid={testid} className="flex items-center gap-3 rounded-xl border px-3 py-2.5" style={{ borderColor: `${color}55`, background: `${color}14` }}>
    <Icon size={18} style={{ color }} />
    <div className="flex-1 text-sm font-semibold" style={{ color }}>{label}</div>
    {sub && <div className="font-mono text-xs text-slate-400">{sub}</div>}
  </div>
);

const RoadRisk = ({ h }) => {
  const { t } = useI18n();
  const lvl = riskLevel(h.road), reasons = roadReasons(h);
  return (
    <div data-testid="road-risk-indicator" className="rounded-xl border border-white/10 bg-black/20 p-3">
      <div className="flex items-center gap-2">
        <TriangleAlert size={16} style={{ color: lvl.color }} />
        <span className="font-heading text-base font-bold uppercase text-white">{t("Yol Durumu")}</span>
        <span className="ml-auto font-heading text-lg font-bold" style={{ color: lvl.color }}>{t(lvl.label)} · {h.road}</span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
        <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${h.road}%`, background: lvl.color }} />
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(reasons.length ? reasons : ["Normal sürüş koşulları"]).map((r) => (
          <span key={r} className="rounded-full bg-white/5 px-2 py-0.5 text-[11px] text-slate-300">{t(r)}</span>
        ))}
      </div>
    </div>
  );
};

const HourChart = ({ hours, cur }) => {
  const { t } = useI18n();
  return (
  <div className="h-40" data-testid="point-chart">
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={hours.map((h) => ({ ...h, hh: h.time.slice(11, 13) }))} margin={{ left: -20, right: 4, top: 6 }}>
        <XAxis dataKey="time" tickFormatter={(v) => v.slice(11, 13)} interval={11} tick={{ fill: "#64748B", fontSize: 10 }} />
        <YAxis yAxisId="t" tick={{ fill: "#64748B", fontSize: 10 }} />
        <YAxis yAxisId="p" orientation="right" hide />
        <Tooltip contentStyle={{ background: "#11161F", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, fontSize: 12 }}
          labelFormatter={(v) => v.replace("T", " ")} />
        <Bar yAxisId="p" dataKey="precipitation" name={t("Yağış mm")} fill="#3B82F6" radius={[2, 2, 0, 0]} />
        <Line yAxisId="t" dataKey="temperature_2m" name={t("Sıcaklık °C")} stroke="#F97316" dot={false} strokeWidth={2} />
        {cur && <ReferenceLine yAxisId="t" x={cur} stroke="#38BDF8" strokeDasharray="3 3" />}
      </ComposedChart>
    </ResponsiveContainer>
  </div>
  );
};

export const PointDrawer = ({ selected, time, reports, onClose, onFavorite }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [view, setView] = useState("hourly");
  const { t } = useI18n();

  useEffect(() => {
    setData(null); setErr(null);
    http.get("/point", { params: { lat: selected.lat.toFixed(3), lon: selected.lon.toFixed(3) } })
      .then((r) => setData(r.data)).catch(() => setErr("Veri alınamadı, tekrar deneyin."));
  }, [selected]);

  const h = data && (data.hours.find((x) => x.time === time) || data.hours[0]);
  const nearby = reports.filter((r) => distKm(r, selected) < 25);
  const fog = h && fogLevel(h.visibility), hail = h && hailLevel(h.hail);

  return (
    <aside data-testid="point-detail-drawer" className="glass drawer-enter absolute bottom-[190px] right-3 sm:bottom-[120px] top-20 z-[1050] flex w-[calc(100%-24px)] flex-col overflow-y-auto rounded-2xl sm:w-[380px]">
      <div className="sticky top-0 z-10 flex items-start gap-3 border-b border-white/5 drawer-head p-4">
        <div className="flex-1">
          <div data-testid="point-detail-title" className="font-heading text-2xl font-extrabold uppercase leading-tight text-white">{selected.name || t("Seçili Nokta")}</div>
          <div className="font-mono text-[11px] text-slate-500">{selected.lat.toFixed(3)}°N · {selected.lon.toFixed(3)}°E{data?.elevation != null && ` · ${Math.round(data.elevation)} m`}</div>
        </div>
        <button data-testid="point-favorite-btn" onClick={onFavorite} className="rounded-full p-2 text-amber-300 hover:bg-amber-400/10" title={t("Favorilere ekle")}><Star size={18} /></button>
        <button data-testid="point-detail-close-btn" onClick={onClose} className="rounded-full p-2 text-slate-400 hover:bg-white/10 hover:text-white"><X size={18} /></button>
      </div>
      {!data && !err && <div className="flex flex-1 items-center justify-center p-10 text-slate-400"><Loader2 className="animate-spin" /></div>}
      {err && <div data-testid="point-detail-error" className="p-6 text-sm text-red-300">{t(err)}</div>}
      {h && (
        <div data-testid="point-view-tabs" className="mx-4 mt-4 grid grid-cols-2 rounded-full bg-black/30 p-1 text-sm">
          {[["hourly", "Seçili Saat"], ["weekly", "7 Günlük"]].map(([k, l]) => (
            <button key={k} data-testid={`point-tab-${k}`} onClick={() => setView(k)}
              className={`rounded-full py-1.5 font-semibold transition-colors ${view === k ? "bg-sky-400 text-slate-950" : "text-slate-400 hover:text-white"}`}>{t(l)}</button>
          ))}
        </div>
      )}
      {h && view === "weekly" && <WeekView days={data.days} />}
      {h && view === "hourly" && (
        <div className="space-y-4 p-4">
          <div className="flex items-baseline justify-between">
            <div data-testid="point-weather-desc" className="text-sm font-semibold text-sky-300">{t(WEATHER_CODES[h.weather_code] || "—")}</div>
            <div className="font-mono text-[11px] text-slate-500">{h.time.replace("T", " ")}</div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Sıcaklık" value={h.temperature_2m?.toFixed(1)} unit="°C" testid="point-temp" />
            <Stat label="Yağış" value={h.precipitation?.toFixed(1)} unit="mm" testid="point-rain" />
            <Stat label="Yağış ihtimali" value={h.precipitation_probability} unit="%" />
            <Stat label="Rüzgar" value={h.wind_speed_10m?.toFixed(0)} unit="km/sa" testid="point-wind" />
            <Stat label="Hamle" value={h.wind_gusts_10m?.toFixed(0)} unit="km/sa" />
            <div className="flex flex-col items-center justify-center rounded-xl border border-white/5 bg-white/[0.03]">
              <Navigation size={22} className="text-cyan-400" style={{ transform: `rotate(${(h.wind_direction_10m || 0) + 180}deg)` }} />
              <span className="mt-1 font-mono text-[10px] text-slate-500">{h.wind_direction_10m}°</span>
            </div>
            <Stat label="Bulut" value={h.cloud_cover} unit="%" />
            <Stat label="Basınç" value={h.pressure_msl?.toFixed(0)} unit="hPa" />
            <Stat label="Nem" value={h.relative_humidity_2m} unit="%" />
            {(h.snowfall > 0 || h.snow_depth > 0) && <>
              <Stat label="Kar" value={h.snowfall?.toFixed(1)} unit="cm/sa" testid="point-snowfall" />
              <Stat label="Kar kalınlığı" value={h.snow_depth != null ? Math.round(h.snow_depth * 100) : null} unit="cm" testid="point-snowdepth" />
            </>}
          </div>
          <div className="space-y-2">
            <Badge testid="hail-warning-badge" icon={CloudHail} label={t(hail.label)} color={hail.color} sub={`${h.hail ?? 0}%`} />
            <Badge testid="fog-visibility-badge" icon={CloudFog} label={t(fog.label)} color={fog.color} sub={h.visibility != null ? `${(h.visibility / 1000).toFixed(1)} ${t("km görüş")}` : ""} />
          </div>
          <RoadRisk h={h} />
          {h.wave_height != null && (
            <div data-testid="point-marine" className="grid grid-cols-3 gap-2">
              <Stat label="Dalga" value={h.wave_height?.toFixed(1)} unit="m" />
              <Stat label="Periyot" value={h.wave_period?.toFixed(0)} unit="sn" />
              <Stat label="Deniz suyu" value={h.sea_surface_temperature?.toFixed(1)} unit="°C" />
            </div>
          )}
          <div>
            <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("72 saatlik tahmin")}</div>
            <HourChart hours={data.hours} cur={h.time} />
          </div>
          <div data-testid="point-nearby-reports">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("Yakındaki raporlar (25 km)")}</div>
            {nearby.length === 0 ? <div className="text-xs text-slate-500">{t("Bu bölgede güncel topluluk raporu yok.")}</div> :
              nearby.slice(0, 6).map((r) => {
                const T = REPORT_TYPES[r.type];
                return (
                  <div key={r.id} className="mb-1.5 flex items-center gap-2 text-sm">
                    <T.icon size={14} style={{ color: T.color }} />
                    <span className="text-slate-200">{t(T.label)}</span>
                    <span className="ml-auto font-mono text-[10px] text-slate-500">{distKm(r, selected).toFixed(1)} km</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </aside>
  );
};
